import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import SimplePageHeader2 from '@/components/SimplePageHeader2'
import { useCreatePlace } from '@/hooks/useCreatePlace'
import { useGQL_teams } from '@/contexts/graphql-and-swr-apis'
import { SimpleButton } from '@unwrittenmedia/simple-components'
import { PlaceAddressForm } from '@/components/team/PlaceAddressForm'
import { PlaceMapPreview } from '@/components/team/PlaceMapPreview'
import { useGooglePlacesAddress } from '@/hooks/useGooglePlacesAddress'
import { PageContentWrapper } from '@unwrittenmedia/simple-components'

type AddPlaceSearch = {
  team: string
}

export const Route = createFileRoute('/venues/add-place')({
  component: AddPlacePage,
  validateSearch: (search: Record<string, unknown>): AddPlaceSearch => {
    return {
      team: (search.team as string) || '',
    }
  },
})

function AddPlacePage() {
  const { team: teamId } = Route.useSearch()
  const navigate = useNavigate()
  const { data: teamsData } = useGQL_teams()
  const { createPlace, isLoading, error } = useCreatePlace({
    onSuccess: () => navigate({ to: '/venues', search: { team: teamId } }),
  })

  const [placeName, setPlaceName] = useState('')
  
  const {
    address,
    setAddress,
    placeDetails,
    isLoaded: isGoogleLoaded,
    containerRef,
  } = useGooglePlacesAddress()

  // Find the team name from the teams data
  const team = teamsData?.teams?.find(t => t.id === teamId)
  const teamName = team?.name || 'Unknown Team'

  const handleCreatePlace = async () => {
    if (!placeName.trim() || !placeDetails) {
      return
    }

    const lat = placeDetails.location?.lat() ?? 0
    const lng = placeDetails.location?.lng() ?? 0

    try {
      await createPlace({
        teamId,
        name: placeName,
        address,
        latitude: lat,
        longitude: lng,
        placeId: placeDetails.id,
      })
    } catch (err) {
      console.error('Failed to create place:', err)
    }
  }

  const canSubmit = placeName.trim() && placeDetails && !isLoading

  return (
    <PageContentWrapper>
      <SimplePageHeader2
        title={`Add Screen: ${teamName}`}
        description="Add a physical location where your team hosts events."
        actionButtonText="Cancel"
        actionButtonColor="danger"
        actionButtonOnClick={() => navigate({ to: '/venues', search: { team: teamId } })}
      />

      <div className="mt-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Form */}
          <div className="bg-white dark:bg-mist-900 shadow rounded-lg ring-1 ring-gray-200 dark:ring-mist-800 overflow-hidden">
            <div className="px-4 py-5 sm:p-6">
              {/* Place Name Input */}
              <div className="mb-6">
                <label
                  htmlFor="place-name"
                  className="block text-sm font-medium text-gray-900 dark:text-white"
                >
                  Screen Name
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="place-name"
                    id="place-name"
                    value={placeName}
                    onChange={(e) => setPlaceName(e.target.value)}
                    placeholder="e.g., Main Campus, Downtown Location"
                    className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-mist-700 placeholder:text-gray-400 dark:placeholder:text-mist-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm/6 bg-white dark:bg-mist-950"
                  />
                </div>
              </div>

              {/* Address Form Component */}
              <PlaceAddressForm
                address={address}
                isGoogleLoaded={isGoogleLoaded}
                containerRef={containerRef}
                placeDetails={placeDetails}
              />

              {/* Error Message */}
              {error && (
                <div className="mt-6 rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                  <div className="text-sm text-red-800 dark:text-red-400">
                    {error}
                  </div>
                </div>
              )}
            </div>

            {/* Horizontal Divider */}
            <div className="border-t border-gray-200 dark:border-mist-800" />

            {/* Bottom Create Button */}
            <div className="px-4 py-4 sm:px-6 bg-gray-50 dark:bg-mist-950/50">
              <SimpleButton
                onClick={handleCreatePlace}
                disabled={!canSubmit}
                className="w-full"
                color="primary"
                size="md"
              >
                {isLoading ? 'Creating...' : 'Create Screen'}
              </SimpleButton>
            </div>
          </div>

          {/* Right Column - Map */}
          <div className="bg-white dark:bg-mist-900 shadow rounded-lg ring-1 ring-gray-200 dark:ring-mist-800 overflow-hidden">
            <PlaceMapPreview
              placeDetails={placeDetails}
              isGoogleLoaded={isGoogleLoaded}
            />
          </div>
        </div>
      </div>
    </PageContentWrapper>
  )
}
